<script async setup lang="ts">
import {type Component, computed, inject, onErrorCaptured, provide, ref} from "vue";
import {viewRepositoryKey} from "@/keys";
import {contextKey, useContext} from "@hantera/portal-app";
import {StackLayer} from "@/view-managers/stack/types";

const props = defineProps<{
  layer: StackLayer
}>();

const context = useContext()
const viewRepository = inject(viewRepositoryKey, { } as any)


provide(contextKey, {
  ...context,
  currentView: computed(() => {
    return props.layer.viewContext
  }),
})

const state = computed(() => viewRepository.getView(context, props.layer.viewContext.path))

const viewKey = computed(() => props.layer.viewContext.uri)

const error = ref<{
  component: string
  message: string
  stack?: string
}>();

onErrorCaptured((err, instance) => {
  error.value = {
    component: instance?.$.type.__name ?? 'unknown',
    message: err.message,
    stack: err.stack
  };
  console.error(err)

  return false;
})
</script>

<template>

  <div class="view-container neat-scroll" :ref="el => layer.element = el as HTMLElement">
    <template v-if="state.state === 'notFound'">
      View not found ({{layer.viewContext.uri}})
    </template>
    <template v-else-if="state.state === 'unauthorized'">
      <div class="view-error-container">
        <div class="view-error box-content box-pop">
          <h2>Unable to display</h2>
          <p>
          You do not have permission to view this page
          </p>
        </div>
      </div>
    </template>
    <template v-else-if="error">
      <div class="view-error">
        <h2>Error in '{{error.component}}' from view '{{layer.viewContext.uri}}'</h2>
  
        <p>
          {{ error.message }}
        </p>
        <pre><code v-if="error.stack">{{ error.stack }}</code></pre>
      </div>
    </template>
    <template v-else>
      <Suspense>
        <component :is="state.component!" :key="viewKey" v-bind="state.props" />
      </Suspense>
    </template>
  </div>


</template>

<style scoped>
.view-container {
  container-type: inline-size;
  position: relative;
  height: 100%;
  width: 100%;
  overflow: auto;
}

.view-error-container {
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.view-error {
  border-color: var(--color-error-50);
  padding: 10px;
  border-radius: 10px;
}

.view-error h2 {
  font-size: 16px;
  font-weight: bold;
  margin: 5px 0;
}

</style>